import React, { useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Row } from 'react-bootstrap';
import { assertPublicRecoveryJwk } from './payloadSafety.ts';
import { formatDateTime } from './recoveryUi.ts';
import type {
  GetDeviceCustodyPublicKey,
  RecoveryDevice,
  RegisterRecoveryDeviceInput,
} from './types.ts';

export interface RecoveryDevicesProps {
  devices: RecoveryDevice[];
  getCustodyPublicKeyJwk?: GetDeviceCustodyPublicKey;
  onRegister: (input: RegisterRecoveryDeviceInput) => Promise<void>;
  onRevoke: (device: RecoveryDevice) => Promise<void>;
}

export default function RecoveryDevices({
  devices,
  getCustodyPublicKeyJwk,
  onRegister,
  onRevoke,
}: RecoveryDevicesProps) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const proposedName = name.trim();
    if (!proposedName) {
      setError('Enter a recognizable device label.');
      return;
    }
    if (!getCustodyPublicKeyJwk) {
      setError(
        'Registration is unavailable until device crypto can supply a custody public key.'
      );
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const custodyPublicKeyJwk =
        await getCustodyPublicKeyJwk(proposedName);
      if (!custodyPublicKeyJwk?.kty) {
        throw new Error(
          'This device did not supply a valid custody public key. Nothing was registered.'
        );
      }
      assertPublicRecoveryJwk(custodyPublicKeyJwk, 'The device custody key');
      await onRegister({
        name: proposedName,
        custodyPublicKeyJwk,
        stewardEligible: false,
      });
      setName('');
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not register this device.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (device: RecoveryDevice) => {
    setBusy(true);
    setError(null);
    try {
      await onRevoke(device);
      setConfirmingId(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not revoke this device.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="recovery-devices-heading">
      <h2 id="recovery-devices-heading" className="h3">
        Devices &amp; alerts
      </h2>
      <Alert variant="warning">
        Browser push can supplement in-app and account alerts, but Web Push
        delivery is not guaranteed. Do not treat a push subscription as proof
        that an owner or keyholder saw a recovery event.
      </Alert>

      <Row className="g-4">
        <Col lg={5}>
          <Card>
            <Card.Body>
              <Card.Title as="h3" className="h5">
                Register this device
              </Card.Title>
              <p>
                A device label identifies a recovery-drill target. The caller’s
                crypto integration must generate or retrieve the public key;
                this form never asks for private key material.
              </p>
              <Form onSubmit={handleRegister}>
                <Form.Group controlId="recovery-device-name">
                  <Form.Label>Device label</Form.Label>
                  <Form.Control
                    value={name}
                    maxLength={80}
                    autoComplete="off"
                    placeholder="For example, Kari’s home iPad"
                    onChange={(event) => setName(event.target.value)}
                  />
                  <Form.Text>
                    To change a label with this first API, revoke the old entry
                    and register the device again.
                  </Form.Text>
                </Form.Group>
                <Form.Check
                  className="my-3"
                  id="recovery-device-steward"
                  type="checkbox"
                  disabled
                  checked={false}
                  readOnly
                  label="Allow incapacity steward sessions (unavailable)"
                />
                <Button
                  type="submit"
                  disabled={busy || !getCustodyPublicKeyJwk}
                >
                  {busy ? 'Registering…' : 'Register device'}
                </Button>
              </Form>
              {!getCustodyPublicKeyJwk && (
                <p className="small text-body-secondary mt-2 mb-0">
                  Disabled until the parent device-crypto callback is connected.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <h3 className="h5">Registered devices</h3>
          {devices.length ? (
            <ListGroup>
              {devices.map((device) => (
                <ListGroup.Item key={device.id}>
                  <div className="d-flex flex-wrap justify-content-between gap-3">
                    <div>
                      <strong>{device.name}</strong>
                      <span className="d-block small text-body-secondary recovery-fingerprint">
                        Fingerprint: {device.fingerprint || 'not provided by server'}
                      </span>
                      <span className="d-block small text-body-secondary">
                        Registered {formatDateTime(device.registeredAt)}
                        {device.lastSeenAt
                          ? ` · Last seen ${formatDateTime(device.lastSeenAt)}`
                          : ''}
                      </span>
                      <Badge bg="secondary" className="mt-2">
                        Steward eligibility unavailable
                      </Badge>
                    </div>
                    <div className="align-self-center">
                      {confirmingId === device.id ? (
                        <div
                          className="d-flex flex-wrap gap-2"
                          role="group"
                          aria-label={`Confirm revoking ${device.name}`}
                        >
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={busy}
                            onClick={() => handleRevoke(device)}
                          >
                            Confirm revoke
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={busy}
                            onClick={() => setConfirmingId(null)}
                          >
                            Keep device
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busy}
                          onClick={() => setConfirmingId(device.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p className="recovery-empty">
              No devices are registered for authenticated recovery drills.
            </p>
          )}
        </Col>
      </Row>

      <div aria-live="polite" aria-atomic="true">
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      </div>
    </section>
  );
}
